import { Component, ViewEncapsulation, ChangeDetectionStrategy, AfterContentInit, Input } from "@angular/core";

import { ArgTypes } from "@storybook/angular/types-6-0";

import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";

import { ListsElementAccessor } from "../lists-accessor";

export const argTypesOptionGroup: ArgTypes = <const>{
  determinate: {
    control: "boolean",
    description: "Whether the control is allowed to be empty after dirty.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  fluid: {
    control: "boolean",
    description: "Whether the element width should be fluid or fixed.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  horizontal: {
    control: "boolean",
    description: "Whether the elements should be stacked horizontally.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
  small: {
    control: "boolean",
    description: "Whether the font-size should be smaller.",
    defaultValue: false,
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
};

/**
 * Option groups display a list of choices on permanent surfaces.
 *
 * Use option groups to:
 *
 * * Provide a list of related options
 * * Where the options are few
 *
 * Option groups can be navigated using arrow keys. Selects should be used instead of options if the number of options are more than 6.
 *
 * Option groups can be used as an Angular `FormControl`, in both reactive and template driven forms.
 */
@Component({
  selector: "ui-input[type=option-group]",
  templateUrl: "./option-group.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        min-width: 100%;
      }
    `,
  ],
})
export class InputOptionGroupComponent extends ListsElementAccessor implements AfterContentInit {
  /**
   * Whether the control is allowed to be empty after dirty.
   *
   * Default is `false`.
   */
  @Input()
  get determinate(): boolean {
    return this._determinate;
  }
  set determinate(value: BooleanInput) {
    this._determinate = coerceBooleanProperty(value);
  }
  private _determinate = false;
  /**
   * Whether the element width should be fluid or fixed.
   *
   * Default is `false`.
   */
  @Input()
  get fluid(): boolean {
    return this._fluid;
  }
  set fluid(value: BooleanInput) {
    this._fluid = coerceBooleanProperty(value);
  }
  private _fluid = false;
  /**
   * Whether the elements should be stacked horizontally.
   *
   * Default is `false`.
   */
  @Input()
  get horizontal(): boolean {
    return this._horizontal;
  }
  set horizontal(value: BooleanInput) {
    this._horizontal = coerceBooleanProperty(value);
  }
  private _horizontal = false;
  /**
   * Whether the font-size should be smaller.
   *
   * Default is `false`.
   */
  @Input()
  get small(): boolean {
    return this._small;
  }
  set small(value: BooleanInput) {
    this._small = coerceBooleanProperty(value);
  }
  private _small = false;
  /**
   * Callback function invoked when navigating the listbox using keyboard.
   *
   * Enables keyboard navigation that behaves similarly as a native select element.
   */
  onNavigate(event: KeyboardEvent) {
    const labelElement = event.target as HTMLLabelElement;
    const inputElement = labelElement.querySelector("input") as HTMLInputElement;
    const previousElementSibling = labelElement.previousElementSibling as HTMLLabelElement | null;
    const nextElementSibling = labelElement.nextElementSibling as HTMLLabelElement | null;
    const firstElementChild = labelElement.parentNode?.firstElementChild as HTMLLabelElement | null;
    const lastElementChild = labelElement.parentNode?.lastElementChild as HTMLLabelElement | null;
    // Switch between navigation key to focus the next or previous element in list
    switch (event.code) {
      // Go to previous option or the last option if the current option is the first in the list
      case "ArrowUp":
      case "ArrowRight": {
        labelElement.tabIndex = -1;
        if (previousElementSibling) {
          previousElementSibling.tabIndex = 0;
          previousElementSibling.focus();
        } else if (lastElementChild) {
          lastElementChild.tabIndex = 0;
          lastElementChild.focus();
        }
        break;
      }
      // Go to next option or the first option if the current option is the last in the list
      case "ArrowDown":
      case "ArrowLeft": {
        labelElement.tabIndex = -1;
        if (nextElementSibling) {
          nextElementSibling.tabIndex = 0;
          nextElementSibling.focus();
        } else if (firstElementChild) {
          firstElementChild.tabIndex = 0;
          firstElementChild.focus();
        }
        break;
      }
      // Selected the focused option
      case "Enter":
      case "Space": {
        labelElement.tabIndex = -1;
        if (firstElementChild) {
          firstElementChild.tabIndex = 0;
        }
        this.onSelected(event, inputElement);
        break;
      }
    }
  }
  /**
   * Callback function invoked when an option has been selected.
   *
   * Close the select element and resets focus. Emit select change event.
   */
  onSelected(event: Event, inputElement: HTMLInputElement) {
    // Prevent default click event
    event.preventDefault();
    // Prevent event propagation in details element
    event.stopImmediatePropagation();
    super.onTouched();
    // Do not collapse options list when a value has been selected
    if (!this.multiple) {
      const hasSelectionRejected =
        !this.determinate &&
        inputElement.checked &&
        this.inputOptionElements.filter((inputOptionElement) => inputOptionElement.nativeElement.checked).length === 1;
      // Do not deselect a determinate select if there is only one selected value remaining
      if (!hasSelectionRejected) {
        inputElement.checked = !inputElement.checked;
        super.onSelectChange();
      }
    } else {
      inputElement.checked = !inputElement.checked;
      super.onSelectChange();
    }
  }
}
